import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../Components/Breadcrumb';
import SEO, { SITE_NAME } from '../../Components/SEO';
import './reading-comprehension.css';
import Readingcomp from "./Data/readingcomp.jsx";

const ReadingComp1 = () => {
    const [searchParams] = useSearchParams();

    const initialTopic = searchParams.get('topic') || 'all';
    const initialLevel = searchParams.get('level') || 'all';
    const initialReadingcompTitle = searchParams.get('readingcompTitle') || '';

    const [readings, setReadings] = useState([]);
    const [currentReadingIndex, setCurrentReadingIndex] = useState(0);
    const [showVocab, setShowVocab] = useState(false);

    useEffect(() => {
        const filteredAndSortedReadings = Readingcomp.filter(reading => {
            const matchesTopic = initialTopic === 'all' || reading.topic.toLowerCase() === initialTopic.toLowerCase();
            const matchesLevel = initialLevel === 'all' || reading.level.toLowerCase() === initialLevel.toLowerCase();
            return matchesTopic && matchesLevel;
        });

        if (initialReadingcompTitle) {
            filteredAndSortedReadings.sort((a, b) => {
                const aMatches = a.readingcompTitle.toLowerCase() === initialReadingcompTitle.toLowerCase();
                const bMatches = b.readingcompTitle.toLowerCase() === initialReadingcompTitle.toLowerCase();
                if (aMatches && !bMatches) return -1;
                if (!aMatches && bMatches) return 1;
                return 0;
            });
        }

        setReadings(filteredAndSortedReadings);
        setCurrentReadingIndex(0);
    }, [initialTopic, initialLevel, initialReadingcompTitle]);

    const currentReading = readings[currentReadingIndex];

    if (!currentReading) {
        return (
            <div className="reading-comprehension-container">
                <Breadcrumb />
                <div className="reading-empty">
                    <p>Loading Reading Comprehension exercise or no data found...</p>
                    <p>Initial Topic: {initialTopic}</p>
                    <p>Initial Level: {initialLevel}</p>
                    <p>Initial Title: {initialReadingcompTitle}</p>
                    {readings.length === 0 && <p>Readings array is empty after filter.</p>}
                </div>
            </div>
        );
    }

    // Collect questions across all paragraphs (some entries put questions only in paragraph 0)
    const allQuestions = currentReading.paragraphs.flatMap(p => p.questions || []);

    // Normalize level for badge class (handle "b1-b2" style values)
    const levelClass = String(currentReading.level || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    return (
        <div className="reading-comprehension-container">
            <SEO
                title="Free English Reading Comprehension Exercises"
                description="Free English reading comprehension exercises with vocabulary support. Practise reading by topic and level, suitable for students and teachers."
                name={SITE_NAME}
                type="article"
            />
            <Breadcrumb />

            <header className="reading-header">
                <div className="reading-header-top">
                    <h1 className="reading-title">{currentReading.readingcompTitle}</h1>
                    <span className={`reading-level-badge ${levelClass}`}>
                        {currentReading.level}
                    </span>
                </div>
                <div className="reading-meta">
                    {currentReading.topic && (
                        <span className="reading-meta-item">
                            <i className="fa fa-tag"></i> {currentReading.topic}
                        </span>
                    )}
                </div>
            </header>

            {currentReading.vocabulary && currentReading.vocabulary.length > 0 && (
                <>
                    <button
                        className="vocab-toggle-btn"
                        onClick={() => setShowVocab(true)}
                    >
                        <i className="fa fa-book"></i> View Vocabulary ({currentReading.vocabulary.length})
                    </button>

                    {showVocab && (
                        <div className="vocab-overlay" onClick={() => setShowVocab(false)}>
                            <div className="vocab-modal" onClick={(e) => e.stopPropagation()}>
                                <button
                                    className="close-vocab-btn"
                                    onClick={() => setShowVocab(false)}
                                    aria-label="Close vocabulary"
                                >
                                    ×
                                </button>
                                <h3>Vocabulary</h3>
                                <ul className="vocabulary-list">
                                    {currentReading.vocabulary.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </>
            )}

            <article className="reading-text-box">
                {currentReading.paragraphs.map((paragraph, index) => {
                    const textContent = Array.isArray(paragraph.text)
                        ? paragraph.text.join('')
                        : paragraph.text || '';
                    return (
                        <div
                            key={index}
                            dangerouslySetInnerHTML={{ __html: textContent }}
                        />
                    );
                })}
            </article>

            {allQuestions.length > 0 && (
                <section className="questions-section">
                    <h2 className="questions-heading">Questions</h2>
                    <ol className="questions-box">
                        {allQuestions.map((q, index) => (
                            <li key={index} className="question-item">
                                <p className="question-text">{q.question}</p>
                                {q.choices && q.choices.length > 0 && (
                                    <ul className="question-choices">
                                        {q.choices.map((choice, ci) => (
                                            <li key={ci} className="question-choice">
                                                {choice}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ol>
                </section>
            )}
        </div>
    );
};

export default ReadingComp1;